'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { AddToListModal } from 'src/components';
import { StrategyLoadingState } from '../StrategyLoadingState';
import { useProfileStrategies } from 'src/hooks/strategy';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useLogging } from 'src/context/logging';
import { useSite } from 'src/context/providers/SiteProvider';
import {
  StrategyResultsHeader,
  ResultsPagination,
  AddToListSection,
} from './components';
import { StrategyMemberCard } from 'src/components/Strategy/shared';

interface SortValues {
  sort: string;
  order: 'asc' | 'desc';
  sort_metric?: string | null;
}

interface StrategyProfileResultsProps {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  activeMarker: string | null;
  setActiveMarker: (markerId: string | null) => void;
  search: string;
  setSearch: (search: string) => void;
  sortValues: SortValues;
  setSortValues: (values: SortValues) => void;
  listMembersResponse: any;
  isFetching?: boolean;
  direction?: string | null;
  strategyMetrics?: any;
}

const StrategyProfileResults = ({
  currentPage,
  setCurrentPage,
  pageSize,
  activeMarker,
  setActiveMarker,
  search,
  setSearch,
  sortValues,
  setSortValues,
  listMembersResponse,
  isFetching = false,
  direction,
  strategyMetrics,
}: StrategyProfileResultsProps) => {
  const log = useLogging();

  const [isPageDropdownOpen, setIsPageDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [showAddToListDialog, setShowAddToListDialog] = useState(false);

  const {
    setSelectedProvider,
    selectedProviders,
    clearSelectedProviders,
    metricToggles,
  } = useProfileStrategies();

  const { closeAllButCurrent } = useDrawer();
  const { dispatch } = useSite();

  const handleAddToLists = () => {
    setShowAddToListDialog(true);
  };

  const handleAddToListSuccess = (result: { addedCount: number; targetListName: string }) => {
    log.event('Providers added to list', {
      listName: result.targetListName,
      count: result.addedCount,
    });
    clearSelectedProviders();
  };

  const sortOptions = useMemo(() => {
    const options: Array<{
      label: string;
      value: string;
      order: 'asc' | 'desc';
      metricId?: string;
    }> = [
      { label: 'Date Added (Newest)', value: 'date_added', order: 'desc' },
      { label: 'Date Added (Oldest)', value: 'date_added', order: 'asc' },
    ];

    if (strategyMetrics?.results?.length > 0) {
      const firstMetric = strategyMetrics.results[0];
      options.push(
        {
          label: 'Patients (Highest)',
          value: 'metric',
          order: 'desc',
          metricId: firstMetric.id.toString(),
        },
        {
          label: 'Patients (Lowest)',
          value: 'metric',
          order: 'asc',
          metricId: firstMetric.id.toString(),
        }
      );
    }

    return options;
  }, [strategyMetrics]);

  const handleSortChange = (
    field: string,
    order: 'asc' | 'desc',
    metricId?: string
  ) => {
    let metricCode: string | null = null;

    if (field === 'metric' && metricId) {
      const metric = strategyMetrics?.results?.find(
        (m: any) => m.id.toString() === metricId
      );

      if (metric?.display_options?.length > 0) {
        const selectedDisplayOption = metricToggles?.[metricId];

        const validOption = metric.display_options.find(
          (option: any) => option.code === selectedDisplayOption
        );

        metricCode = validOption
          ? validOption.code
          : metric.display_options[0].code;
      }
    }

    if (field === 'metric') {
      setSortValues({
        sort: 'metric',
        order: order,
        sort_metric: metricCode,
      });
    } else {
      setSortValues({
        sort: field,
        order: order,
        sort_metric: null,
      });
    }
    setIsSortDropdownOpen(false);
  };

  const currentSortLabel = useMemo(() => {
    if (sortValues.sort === 'date_added') {
      return sortValues.order === 'desc'
        ? 'Date Added (Newest)'
        : 'Date Added (Oldest)';
    }
    if (sortValues.sort === 'metric' && sortValues.sort_metric) {
      return sortValues.order === 'desc'
        ? 'Patients (Highest)'
        : 'Patients (Lowest)';
    }
    return 'Date Added (Newest)';
  }, [sortValues]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, setCurrentPage]);

  const listMembers = listMembersResponse?.results || [];
  const totalCount = listMembersResponse?.count || 0;
  const displayCount = totalCount;
  const numberOfPages = Math.ceil(displayCount / pageSize);
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, displayCount);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setIsPageDropdownOpen(false);
  };

  const handleCardClick = (member: any) => {
    setSelectedProvider(member);
    setActiveMarker(member.provider_id?.toString());
    dispatch({
      type: 'SET_CURRENT_PROVIDER',
      payload: { id: member.provider_id, type: member.type || null },
    });
    closeAllButCurrent('profile');
  };

  return (
    <div className="w-96 h-full bg-neutral-50 z-10 pointer-events-auto rounded-br-lg">
      <div className="h-full inline-flex flex-col items-start overflow-hidden">
        <div className="w-96 flex-1 bg-neutral-50 flex flex-col items-start overflow-hidden">
          <StrategyResultsHeader
            displayCount={displayCount}
            isFetching={isFetching}
            search={search}
            setSearch={setSearch}
            sortOptions={sortOptions}
            currentSortLabel={currentSortLabel}
            handleSortChange={handleSortChange}
            sortValues={sortValues}
            strategyMetrics={strategyMetrics}
            isSortDropdownOpen={isSortDropdownOpen}
            setIsSortDropdownOpen={setIsSortDropdownOpen}
          />

          <div className="w-full flex-1 overflow-y-auto">
            {isFetching ? (
              <StrategyLoadingState size="md" />
            ) : (
              <div className="flex flex-col gap-2 px-4 pb-4">
                {listMembers && listMembers?.length > 0 ? (
                  listMembers.map((member: any) => (
                    <StrategyMemberCard
                      key={member.id || member.provider_id}
                      member={member}
                      direction={direction}
                      strategyMetrics={strategyMetrics}
                      isActive={
                        activeMarker === member.provider_id?.toString()
                      }
                      onSelect={() => handleCardClick(member)}
                      onHover={() =>
                        setActiveMarker(member.provider_id?.toString())
                      }
                      onHoverEnd={() => setActiveMarker(null)}
                    />
                  ))
                ) : (
                  <div className="flex items-center justify-center h-32 text-neutral-500">
                    No opportunities found
                  </div>
                )}
              </div>
            )}
          </div>

          <ResultsPagination
            currentPage={currentPage}
            numberOfPages={numberOfPages}
            totalCount={totalCount}
            startIndex={startIndex}
            endIndex={endIndex}
            displayCount={displayCount}
            handlePageChange={handlePageChange}
            isPageDropdownOpen={isPageDropdownOpen}
            setIsPageDropdownOpen={setIsPageDropdownOpen}
          />

          <AddToListSection
            selectedProviders={selectedProviders}
            handleAddToLists={handleAddToLists}
          />
        </div>
      </div>

      <AddToListModal
        isOpen={showAddToListDialog}
        onClose={() => setShowAddToListDialog(false)}
        mode="add"
        providers={selectedProviders}
        onSuccess={handleAddToListSuccess}
      />
    </div>
  );
};

export default StrategyProfileResults;
